import { uniqBy } from 'lodash-es';
import { isUndef } from '@aviata/type-guards';
import type { Airflow } from '@aviata/platform-types';

type SerializedColumnType = 'aisle' | 'window' | 'middle' | 'standard';

type SerializedRowType =
  | 'exit'
  | 'extraLegroom'
  | 'notExist'
  | 'standard'
  | 'premium'
  | 'wing';

type SerializedSeatType = 'aisle' | 'standard';

type ColDict = Record<SerializedColumnType, string[]>;

const colDict: ColDict = {
  aisle: ['A', 'AS', 'NPW', 'Aisle', 'AISLE'],
  window: ['W', 'WN'],
  middle: ['M', 'MD', '9', 'CenterSeat'],
  standard: ['S', 'ST', 'STD', 'STANDARD'],
};

type RowDict = Record<SerializedRowType, string[]>;

const rowDict: RowDict = {
  exit: ['E', 'EX', 'EXIT', 'EXIT ROW', 'ExitRow'],
  extraLegroom: [
    'XL',
    'XLE',
    'EXTRA LEGROOM',
    'EXTRA LEG ROOM',
    'ExtraLegRoom',
  ],
  notExist: ['8', 'Z'],
  standard: ['S', 'ST', 'STD', 'STANDARD'],
  wing: ['K', 'W'],
  premium: [],
};

export interface SerializedSeat extends Partial<Airflow.SeatMap.Seat> {
  key: string;
  cabinClass?: Airflow.CabinClass;
  coordinates: {
    row: number;
    col: string;
  };
  type: SerializedSeatType;
}

export interface SerializedColumn extends Partial<Airflow.SeatMap.Column> {
  type: SerializedColumnType;
}

export interface SerializedRow {
  number: number;
  type: SerializedRowType;
  seats: SerializedSeat[];
  characteristics?: string | null;
}

export interface SerializedSeatmap {
  rows: SerializedRow[];
  cols: SerializedColumn[];
}

function getColType(col: Airflow.SeatMap.Column) {
  for (const [key, value] of Object.entries(colDict)) {
    if (value.includes(col.characteristics)) {
      return key as SerializedColumnType;
    }
  }
  return 'standard';
}

function getRowType(row: Airflow.SeatMap.Row) {
  for (const [key, value] of Object.entries(rowDict)) {
    if (value.includes(row.characteristics || '')) {
      return key as SerializedRowType;
    }
  }
  return 'standard';
}

function getAisleIndex(
  cols: Airflow.SeatMap.Column[],
  col: Airflow.SeatMap.Column,
  idx: number,
) {
  const isPreviousNearAnAisle =
    cols[idx - 1] && getColType(cols[idx - 1]) === 'aisle';
  const isCurrentNearAnAisle = col && getColType(col) === 'aisle';
  const isNextNearAnAisle =
    cols[idx + 1] && getColType(cols[idx + 1]) === 'aisle';

  const isFirstCol = idx === 0;
  const isLastCol = idx === cols.length - 1;

  // if it's the first column and an aisle and the next column is not an aisle
  if (isFirstCol && isCurrentNearAnAisle && !isNextNearAnAisle) {
    // insert aisle before the first column
    return 0;
  }

  // if it's the last column and an aisle and the previous column is not an aisle
  if (isLastCol && isCurrentNearAnAisle && !isPreviousNearAnAisle) {
    // insert aisle after the last column
    return cols.length;
  }

  // if it's an aisle and the next column is an aisle and the previous column is not an aisle
  if (isCurrentNearAnAisle && isNextNearAnAisle) {
    // insert aisle before the current column
    return idx + 1;
  }
}

function serializeSeats({
  rowNumber,
  seats,
  cols,
  cabinClass,
}: {
  rowNumber: number;
  seats: [key: string, value: Airflow.SeatMap.Seat][];
  cols: Airflow.SeatMap.Column[];
  cabinClass: Airflow.CabinClass;
}) {
  const serializedSeats: SerializedSeat[] = seats.map(([key, value]) => {
    return {
      ...value,
      key: `${rowNumber}${key}`,
      cabinClass,
      coordinates: {
        row: rowNumber,
        col: key,
      },
      type: 'standard',
    };
  });

  let aisleCount = 0;
  seats.forEach((value, idx) => {
    const aisleIndex = getAisleIndex(cols, cols[idx], idx);

    if (!isUndef(aisleIndex)) {
      serializedSeats.splice(aisleIndex + aisleCount, 0, {
        key: `${rowNumber}${idx}`,
        cabinClass,
        price: {
          currency: 'KZT',
          amount: '0',
        },
        coordinates: {
          row: rowNumber,
          col: `A${idx}`,
        },
        type: 'aisle',
      });
      aisleCount++;
    }
  });

  return serializedSeats;
}

function serializeCols(cols: Airflow.SeatMap.Column[]) {
  const serializedCols: SerializedColumn[] = [];

  cols.forEach(col => {
    serializedCols.push({
      name: col.name,
      type: 'standard',
    });
  });

  let aisleCount = 0;
  cols.forEach((_col, idx) => {
    const aisleIndex = getAisleIndex(cols, cols[idx], idx);

    if (!isUndef(aisleIndex)) {
      serializedCols.splice(aisleIndex + aisleCount, 0, {
        name: `A${idx}`,
        type: 'aisle',
      });

      aisleCount++;
    }
  });

  return serializedCols;
}

function serializeRows(
  rows: Airflow.SeatMap.Row[],
  cols: Airflow.SeatMap.Column[],
  cabinClass: Airflow.CabinClass,
) {
  return rows.map(row => {
    const serializedSeats = serializeSeats({
      rowNumber: row.number,
      seats: Object.entries(row.seats),
      cabinClass,
      cols,
    });
    return {
      number: row.number,
      seats: serializedSeats,
      characteristics: row.characteristics,
      type: getRowType(row),
    };
  });
}

export function serializeSeatmap(
  seatmapResponse: Airflow.SeatMap.Entity,
): SerializedSeatmap {
  const flatRows = seatmapResponse.items.flatMap(item => {
    return item.rows;
  });

  const flatCols = uniqBy(
    seatmapResponse.items.flatMap(item => {
      return item.columns;
    }),
    'name',
  );

  const cabinClass = seatmapResponse.items[0]?.cabin as Airflow.CabinClass;

  return {
    rows: serializeRows(flatRows, flatCols, cabinClass),
    cols: serializeCols(flatCols),
  };
}
